import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Loader2, User, Target, BookOpen, Frown, Plus, X, Info, BarChart3,
  Route, MapPin, AlertTriangle, Sparkles, Wand2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { PersonaPreviewCard } from '../components/PersonaPreviewCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// List input component for goals, habits, frustrations
const ListInput = ({ label, placeholder, value = [], onChange, icon: Icon }) => {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    if (inputValue.trim()) {
      onChange([...value, inputValue.trim()]);
      setInputValue('');
    }
  };

  const handleRemove = (index) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4" />}
        {label}
      </Label>
      <div className="flex gap-2">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
        />
        <Button type="button" variant="outline" size="icon" onClick={handleAdd}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {value.map((item, index) => (
            <Badge key={index} variant="secondary" className="px-3 py-1">
              {item}
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="ml-2 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

export const PersonaEditor = () => {
  const { selectedProject } = useApp();
  const navigate = useNavigate();
  const { personaId } = useParams();
  const isEditing = !!personaId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingGa4, setLoadingGa4] = useState(false);
  const [ga4Configured, setGa4Configured] = useState(false);
  const [ga4Summary, setGa4Summary] = useState(null);

  // Data source selection
  const [dataSource, setDataSource] = useState('manual');
  const [dateRange, setDateRange] = useState('30');

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    quote: '',
    avatar_url: '',
    age_range: '',
    gender: '',
    location: '',
    job_role: '',
    device_preference: '',
    goals: [],
    learning_habits: [],
    frustrations: [],
    phone_usage_social: null,
    phone_usage_messaging: null,
    phone_usage_learning: null,
    journey_entry_context: '',
    journey_key_pages: [],
    journey_critical_point: '',
    ga4_property_id: null,
    ga4_date_range_start: null,
    ga4_date_range_end: null,
    ga4_notes: null
  });

  useEffect(() => {
    if (selectedProject) {
      checkGa4Configuration();
      if (isEditing) {
        loadPersona();
      }
    }
  }, [selectedProject, personaId]);

  const checkGa4Configuration = async () => {
    try {
      // Check global GA4 settings
      const globalResp = await axios.get(`${API_URL}/api/integrations/ga4/global-settings`);
      if (!globalResp.data.is_active) {
        setGa4Configured(false);
        return;
      }

      // Check project GA4 settings
      const projectResp = await axios.get(
        `${API_URL}/api/integrations/projects/${selectedProject.id}/ga4-settings`
      );
      setGa4Configured(projectResp.data?.is_active || false);
    } catch (error) {
      setGa4Configured(false);
    }
  };

  const loadPersona = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/personas/projects/${selectedProject.id}/personas/${personaId}`
      );
      setFormData(response.data);
      if (response.data.ga4_property_id) {
        setDataSource('ga4');
      }
    } catch (error) {
      toast.error('Erro ao carregar persona');
      navigate('/personas');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateGa4Suggestion = async () => {
    if (!ga4Configured) {
      toast.error('Configure o GA4 para este projeto na aba Relatórios.');
      return;
    }

    // Check if there's existing data that might be overwritten
    const hasExistingData = formData.name || formData.age_range || formData.location || 
                           formData.device_preference || formData.journey_key_pages?.length > 0 ||
                           formData.goals?.length > 0;
    
    if (hasExistingData && !window.confirm('Isso irá substituir os dados existentes com sugestões da IA baseadas no GA4. Os campos do GA4 (idade, localização, dispositivo e páginas-chave) permanecerão com dados reais. Deseja continuar?')) {
      return;
    }

    setLoadingGa4(true);
    setGa4Summary(null);

    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));

      // Use the new AI-assisted endpoint
      const response = await axios.post(
        `${API_URL}/api/personas/projects/${selectedProject.id}/personas/ai-suggest-from-ga4`,
        {
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
        }
      );

      const result = response.data;
      const { ga4, ai, ai_error, avatar, is_mock } = result;

      // Build the GA4 summary for display
      const summaryForDisplay = {
        ga4_summary: ga4.summary,
        device_preference: ga4.preferred_device,
        journey_key_pages: ga4.journey_key_pages,
        is_mock: is_mock
      };

      // Update form with GA4 fields (always from GA4, not AI)
      const updatedFormData = {
        ...formData,
        // GA4-based fields (deterministic, exact values from GA4)
        age_range: ga4.age_range || formData.age_range,
        gender: ga4.gender || formData.gender,
        location: ga4.location || formData.location,
        // Normalize device value to match Select options (capitalize first letter)
        device_preference: ga4.preferred_device 
          ? ga4.preferred_device.charAt(0).toUpperCase() + ga4.preferred_device.slice(1).toLowerCase()
          : formData.device_preference,
        journey_key_pages: ga4.journey_key_pages?.length > 0 
          ? ga4.journey_key_pages 
          : formData.journey_key_pages,
        // GA4 metadata
        ga4_property_id: selectedProject.id,
        ga4_date_range_start: startDate.toISOString().split('T')[0],
        ga4_date_range_end: endDate.toISOString().split('T')[0]
      };

      // If AI succeeded, add AI-suggested fields
      if (ai && !ai_error) {
        updatedFormData.name = ai.name || formData.name;
        updatedFormData.quote = ai.quote || formData.quote;
        updatedFormData.goals = ai.objectives?.length > 0 ? ai.objectives : formData.goals;
        updatedFormData.learning_habits = ai.usage_habits?.length > 0 ? ai.usage_habits : formData.learning_habits;
        updatedFormData.frustrations = ai.frustrations?.length > 0 ? ai.frustrations : formData.frustrations;
        updatedFormData.journey_entry_context = ai.journey_entry_context || formData.journey_entry_context;
        updatedFormData.journey_critical_point = ai.journey_critical_point || formData.journey_critical_point;
        updatedFormData.ga4_notes = 'Dados GA4 + sugestões geradas por IA (GPT-4o)';
        
        // Set avatar if generated
        if (avatar?.url) {
          updatedFormData.avatar_url = avatar.url;
        }
      } else {
        updatedFormData.ga4_notes = 'Dados mapeados do GA4 (IA indisponível)';
      }

      setFormData(updatedFormData);
      setGa4Summary(summaryForDisplay);

      // Show appropriate toast
      if (ai_error) {
        toast.warning(`Dados do GA4 preenchidos. A IA não pôde gerar sugestões: ${ai_error}`);
      } else if (is_mock) {
        toast.success('Persona gerada com sucesso usando dados simulados (mock) do GA4 + IA.');
      } else {
        const avatarMsg = avatar?.url ? ' Avatar gerado!' : '';
        toast.success(`Persona gerada com sucesso usando dados reais do GA4 + IA!${avatarMsg}`);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao gerar persona com IA';
      toast.error(message);
    } finally {
      setLoadingGa4(false);
    }
  };

  // Removed: AI generation function - replaced with direct data mapping

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name?.trim()) {
      toast.error('O nome da persona é obrigatório.');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...formData,
        project_id: selectedProject.id
      };

      if (isEditing) {
        await axios.put(
          `${API_URL}/api/personas/projects/${selectedProject.id}/personas/${personaId}`,
          payload
        );
        toast.success('Persona atualizada com sucesso!');
      } else {
        await axios.post(
          `${API_URL}/api/personas/projects/${selectedProject.id}/personas`,
          payload
        );
        toast.success('Persona criada com sucesso!');
      }

      navigate('/personas');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao salvar persona';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Tem certeza que deseja excluir esta persona?')) {
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/api/personas/projects/${selectedProject.id}/personas/${personaId}`
      );
      toast.success('Persona excluída com sucesso');
      navigate('/personas');
    } catch (error) {
      toast.error('Erro ao excluir persona');
    }
  };

  if (!selectedProject) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Selecione um projeto.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/personas')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">
            {isEditing ? 'Editar Persona' : 'Nova Persona'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEditing 
              ? 'Atualize as informações da persona' 
              : 'Crie uma nova persona para o projeto'}
          </p>
        </div>
      </div>

      {/* Two-column layout - 40% preview, 60% form */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Preview - 5/12 = ~42% */}
        <div className="lg:col-span-5">
          <Card className="sticky top-6">
            <CardHeader className="pb-0">
              <CardTitle className="text-base">Pré-visualização</CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <PersonaPreviewCard persona={formData} />
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Form - 7/12 = ~58% */}
        <div className="lg:col-span-7">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Data Source Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Origem dos dados</CardTitle>
                <CardDescription>
                  Escolha como deseja criar a persona
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup
                  value={dataSource}
                  onValueChange={setDataSource}
                  className="grid grid-cols-2 gap-4"
                >
                  <div>
                    <RadioGroupItem
                      value="manual"
                      id="manual"
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor="manual"
                      className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                    >
                      <User className="mb-2 h-6 w-6" />
                      <span className="text-sm font-medium">Preencher manualmente</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem
                      value="ga4"
                      id="ga4"
                      className="peer sr-only"
                      disabled={!ga4Configured}
                    />
                    <Label
                      htmlFor="ga4"
                      className={`flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer ${!ga4Configured ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Sparkles className="mb-2 h-6 w-6" />
                      <span className="text-sm font-medium">Usar dados do GA4</span>
                      {!ga4Configured && (
                        <span className="text-xs text-muted-foreground mt-1 text-center">
                          Configure o GA4 em Relatórios
                        </span>
                      )}
                    </Label>
                  </div>
                </RadioGroup>

                {/* GA4 Options */}
                {dataSource === 'ga4' && ga4Configured && (
                  <div className="space-y-4 pt-4 border-t">
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <Label>Período</Label>
                        <Select value={dateRange} onValueChange={setDateRange}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="7">Últimos 7 dias</SelectItem>
                            <SelectItem value="30">Últimos 30 dias</SelectItem>
                            <SelectItem value="90">Últimos 90 dias</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1">
                        <Label>&nbsp;</Label>
                        <Button
                          type="button"
                          onClick={handleGenerateGa4Suggestion}
                          disabled={loadingGa4}
                          className="w-full bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-700"
                        >
                          {loadingGa4 ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Gerando com IA...
                            </>
                          ) : (
                            <>
                              <Wand2 className="mr-2 h-4 w-4" />
                              Criar com assistente de IA
                            </>
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Info about AI + GA4 */}
                    <div className="bg-muted/50 rounded-lg p-3 border">
                      <div className="flex gap-2">
                        <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                        <div className="text-xs text-muted-foreground">
                          <p className="font-medium text-foreground mb-1">Campos do GA4 (dados reais):</p>
                          <ul className="list-disc list-inside space-y-0.5">
                            <li>Faixa etária, Localização, Dispositivo</li>
                            <li><strong>TOP 3 páginas-chave</strong> de conversores</li>
                          </ul>
                          <p className="font-medium text-foreground mt-2 mb-1">Campos sugeridos pela IA:</p>
                          <ul className="list-disc list-inside space-y-0.5">
                            <li>Nome, Quote, Objetivos, Hábitos, Frustrações</li>
                            <li>Como chega, Ponto crítico da jornada</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* GA4 Summary Panel */}
                    {ga4Summary && (
                      <div className={`rounded-lg border ${ga4Summary.is_mock ? 'bg-blue-50/50 border-blue-200' : 'bg-green-50/50 border-green-200'}`}>
                        <div className={`px-4 py-2.5 border-b ${ga4Summary.is_mock ? 'border-blue-200 bg-blue-100/50' : 'border-green-200 bg-green-100/50'}`}>
                          <div className="flex items-center gap-2">
                            <BarChart3 className={`h-4 w-4 ${ga4Summary.is_mock ? 'text-blue-600' : 'text-green-600'}`} />
                            <span className={`text-sm font-semibold ${ga4Summary.is_mock ? 'text-blue-900' : 'text-green-900'}`}>
                              Dados mapeados do GA4
                            </span>
                            {ga4Summary.is_mock && (
                              <Badge variant="outline" className="text-xs border-blue-400 text-blue-700">Mock</Badge>
                            )}
                          </div>
                        </div>
                        <div className="p-4">
                          {ga4Summary.ga4_summary && (
                            <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">País principal:</span>
                                <span className="font-medium">{ga4Summary.ga4_summary.top_country || 'não disponível'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Cidade principal:</span>
                                <span className="font-medium">{ga4Summary.ga4_summary.top_city || 'não disponível'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Faixa etária:</span>
                                <span className="font-medium">{ga4Summary.ga4_summary.top_age_range || 'não disponível'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Dispositivo:</span>
                                <span className="font-medium">{ga4Summary.device_preference || 'não disponível'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Usuários que converteram:</span>
                                <span className="font-medium text-primary">{ga4Summary.ga4_summary.converting_users?.toLocaleString('pt-BR') || ga4Summary.ga4_summary.users?.toLocaleString('pt-BR') || '0'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Sessões:</span>
                                <span className="font-medium">{ga4Summary.ga4_summary.sessions?.toLocaleString('pt-BR') || '0'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Taxa de engajamento:</span>
                                <span className="font-medium text-green-600">
                                  {ga4Summary.ga4_summary.engagement_rate != null 
                                    ? `${(ga4Summary.ga4_summary.engagement_rate * 100).toFixed(1).replace('.', ',')}%` 
                                    : 'não disponível'}
                                </span>
                              </div>
                              {ga4Summary.ga4_summary.conversion_event && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Evento de conversão:</span>
                                  <span className="font-medium">{ga4Summary.ga4_summary.conversion_event}</span>
                                </div>
                              )}
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground mt-3 pt-3 border-t">
                            <Info className="h-3 w-3 inline mr-1" />
                            Os campos <strong>faixa etária, localização, dispositivo e TOP 3 páginas-chave</strong> vêm diretamente do GA4. Os demais campos foram sugeridos pela IA com base neste contexto.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* General Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Informações gerais</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome da persona *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ex: Maria Silva"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="avatar_url">URL do avatar (opcional)</Label>
                    <Input
                      id="avatar_url"
                      value={formData.avatar_url}
                      onChange={(e) => setFormData({ ...formData, avatar_url: e.target.value })}
                      placeholder="https://exemplo.com/foto.jpg"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quote">Frase / Quote (opcional)</Label>
                  <Textarea
                    id="quote"
                    value={formData.quote}
                    onChange={(e) => setFormData({ ...formData, quote: e.target.value })}
                    placeholder="Ex: Preciso de soluções práticas que funcionem no meu dia a dia."
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Demographics */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Dados demográficos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="age_range">Faixa etária</Label>
                    <Input
                      id="age_range"
                      value={formData.age_range}
                      onChange={(e) => setFormData({ ...formData, age_range: e.target.value })}
                      placeholder="Ex: 25-34"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gender">Gênero</Label>
                    <Select
                      value={formData.gender}
                      onValueChange={(value) => setFormData({ ...formData, gender: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Feminino">Feminino</SelectItem>
                        <SelectItem value="Masculino">Masculino</SelectItem>
                        <SelectItem value="Outro">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="location">Localização</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="Ex: São Paulo, Brasil"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="job_role">Profissão / Cargo</Label>
                    <Input
                      id="job_role"
                      value={formData.job_role}
                      onChange={(e) => setFormData({ ...formData, job_role: e.target.value })}
                      placeholder="Ex: Gerente de E-commerce"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="device_preference">Dispositivo preferido</Label>
                  <Select
                    value={formData.device_preference}
                    onValueChange={(value) => setFormData({ ...formData, device_preference: value })}
                  >
                    <SelectTrigger className="w-full md:w-1/2">
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mobile">Mobile</SelectItem>
                      <SelectItem value="Desktop">Desktop</SelectItem>
                      <SelectItem value="Tablet">Tablet</SelectItem>
                      <SelectItem value="Outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Goals */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Objetivos</CardTitle>
                <CardDescription>
                  O que essa persona deseja alcançar?
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ListInput
                  label=""
                  placeholder="Ex: Aumentar conhecimento profissional"
                  value={formData.goals}
                  onChange={(goals) => setFormData({ ...formData, goals })}
                  icon={Target}
                />
              </CardContent>
            </Card>

            {/* Learning Habits */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Hábitos de uso / aprendizagem</CardTitle>
                <CardDescription>
                  Como essa persona interage com produtos digitais?
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ListInput
                  label=""
                  placeholder="Ex: Usa o celular em pequenos momentos livres"
                  value={formData.learning_habits}
                  onChange={(learning_habits) => setFormData({ ...formData, learning_habits })}
                  icon={BookOpen}
                />
              </CardContent>
            </Card>

            {/* Frustrations */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Frustrações</CardTitle>
                <CardDescription>
                  Quais são as dores e frustrações dessa persona?
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ListInput
                  label=""
                  placeholder="Ex: Não entende termos técnicos"
                  value={formData.frustrations}
                  onChange={(frustrations) => setFormData({ ...formData, frustrations })}
                  icon={Frown}
                />
              </CardContent>
            </Card>

            {/* Journey & Channels */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Route className="h-4 w-4" />
                  Jornada & canais principais
                </CardTitle>
                <CardDescription>
                  Como essa persona chega, navega e onde a jornada é mais crítica?
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Como chega */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-primary" />
                    Como chega
                  </Label>
                  <Textarea
                    value={formData.journey_entry_context || ''}
                    onChange={(e) => setFormData({ ...formData, journey_entry_context: e.target.value })}
                    placeholder="Ex: Chega principalmente via busca orgânica no Google, pesquisando por termos como 'lingerie confortável'."
                    rows={2}
                  />
                </div>

                {/* Páginas / telas mais importantes */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-primary" />
                    Páginas / telas mais importantes
                  </Label>
                  <ListInput
                    label=""
                    placeholder="Ex: Página de categoria Lingerie"
                    value={formData.journey_key_pages || []}
                    onChange={(journey_key_pages) => setFormData({ ...formData, journey_key_pages })}
                  />
                </div>

                {/* Ponto crítico da jornada */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    Ponto crítico da jornada
                  </Label>
                  <Textarea
                    value={formData.journey_critical_point || ''}
                    onChange={(e) => setFormData({ ...formData, journey_critical_point: e.target.value })}
                    placeholder="Ex: Maior abandono na etapa de seleção de frete no checkout."
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Anotações internas (opcional)</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={formData.ga4_notes || ''}
                  onChange={(e) => setFormData({ ...formData, ga4_notes: e.target.value })}
                  placeholder="Anotações adicionais sobre esta persona..."
                  rows={3}
                />
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-4">
              <div>
                {isEditing && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDelete}
                  >
                    Excluir persona
                  </Button>
                )}
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/personas')}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    'Salvar persona'
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
